import React, { useEffect, useRef, useCallback } from 'react';
import { Users, Sparkles, AlertCircle, Loader2 } from 'lucide-react';
import POCTable from './POCTable';

/**
 * SelectedPOCsList Component
 * Displays selected POCs with lazy loading support
 * Automatically loads more POCs when user scrolls near the bottom
 */
const SelectedPOCsList = ({
  pocs,
  pocBudgets,
  allocationMode,
  columns,
  onBudgetChange,
  onRemovePOC,
  isOverBudget,
  remainingBudget,
  loading,
  hasMore,
  onLoadMore,
  totalPocs,
  BudgetAllocationMode,
  className = '',
}) => {
  const containerRef = useRef(null);
  const loadMoreTriggerRef = useRef(null);

  /**
   * Intersection Observer callback for lazy loading
   * Triggers when the bottom sentinel element becomes visible
   */
  const handleObserver = useCallback((entries) => {
    const target = entries[0];
    if (target.isIntersecting && hasMore && !loading) {
      console.log('Lazy load trigger: Loading more POCs...');
      onLoadMore();
    }
  }, [hasMore, loading, onLoadMore]);

  /**
   * Set up Intersection Observer for lazy loading
   */
  useEffect(() => {
    const options = {
      root: null, // viewport
      rootMargin: '200px', // Start loading 200px before reaching the bottom
      threshold: 0.1,
    };

    const observer = new IntersectionObserver(handleObserver, options);

    if (loadMoreTriggerRef.current) {
      observer.observe(loadMoreTriggerRef.current);
    }

    return () => {
      if (loadMoreTriggerRef.current) {
        observer.unobserve(loadMoreTriggerRef.current);
      }
    };
  }, [handleObserver]);

  if (pocs.length === 0) {
    return (
      <div className="py-20 text-center border-2 border-dashed border-gray-100 rounded-2xl">
        <Users className="mx-auto text-gray-200 mb-4" size={48} />
        <h4 className="text-gray-900 font-bold">No POCs selected yet</h4>
        <p className="text-gray-500 text-sm mb-6">
          {totalPocs > 0 
            ? `${totalPocs} POCs are available for this campaign` 
            : 'POCs will appear here when selected'}
        </p>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`} ref={containerRef}>
      {/* POC Count Header */}
      <div className="flex items-center justify-between pb-2">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-gray-900">
            Selected POCs ({pocs.length}{totalPocs > 0 && ` of ${totalPocs}`})
          </h3>
        </div>
        
        {allocationMode === BudgetAllocationMode?.OPTIMIZED && (
          <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium">
            <Sparkles size={14} />
            Budget Optimized
          </div>
        )}
      </div>

      {/* POC Table */}
      <div className="rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <POCTable
          columns={columns}
          data={pocs}
          rowClassName="hover:bg-gray-50/50 transition-colors"
          maxHeight="600px"
          emptyMessage="No POCs selected"
          className="overflow-x-auto"
        />
        
        {/* Lazy Load Trigger Element */}
        {hasMore && (
          <div 
            ref={loadMoreTriggerRef}
            className="h-10 flex items-center justify-center bg-gray-50 border-t border-gray-200"
          >
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Loading more POCs...</span>
              </div>
            ) : (
              <div className="text-xs text-gray-400">
                Scroll for more
              </div>
            )}
          </div>
        )}
      </div>

      {/* Loading Indicator */}
      {loading && pocs.length > 0 && (
        <div className="text-center py-4">
          <div className="inline-flex items-center gap-2 text-sm text-gray-600">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Loading more POCs...</span>
          </div>
        </div>
      )}

      {/* No More Data Indicator */}
      {!hasMore && pocs.length > 0 && pocs.length >= 10 && (
        <div className="text-center py-3 text-sm text-gray-500">
          All {pocs.length} POCs loaded
        </div>
      )}

      {/* Over Budget Alert */}
      {isOverBudget && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700 text-sm animate-pulse">
          <AlertCircle size={20} />
          <p>
            <span className="font-bold uppercase tracking-tight mr-1">Budget Alert:</span>
            Your allocation exceeds the limit by{' '}
            <span className="font-black underline">
              ${Math.abs(remainingBudget).toLocaleString()}
            </span>
            .
          </p>
        </div>
      )}
    </div>
  );
};

export default SelectedPOCsList;
